import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, imgSrc } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, X } from "lucide-react";

const CATS = [
  { key: "rehearsal", label: "Rehearsal" },
  { key: "photo", label: "Photo / Video" },
  { key: "meeting", label: "Meeting" },
  { key: "fitness", label: "Fitness" },
  { key: "event", label: "Event" },
  { key: "popup", label: "Pop-up" },
  { key: "other", label: "Other" },
];

export default function CreateListing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = Boolean(id);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    title: "", description: "", category: "rehearsal",
    city: "", address: "", price_per_hour: 30, deposit: 0,
    capacity: 10, amenities: "", rules: "",
  });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (editing) {
      api.get("/listings/mine").then((r) => {
        const l = r.data.find((x) => x.id === id);
        if (!l) { toast.error("Listing not found"); navigate("/host"); return; }
        setForm({
          title: l.title || "", description: l.description || "", category: l.category || "rehearsal",
          city: l.city || "", address: l.address || "", price_per_hour: l.price_per_hour ?? 30,
          deposit: l.deposit ?? 0, capacity: l.capacity ?? 10,
          amenities: (l.amenities || []).join(", "), rules: l.rules || "",
        });
        setPhotos(l.photos || []);
        setStatus(l.status);
      }).catch(() => { toast.error("Failed to load listing"); navigate("/host"); });
    }
  }, [user, loading, navigate, editing, id]);

  const upload = async (files) => {
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data.path]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const buildBody = () => ({
    ...form,
    price_per_hour: Number(form.price_per_hour),
    deposit: Number(form.deposit) || 0,
    capacity: Number(form.capacity),
    amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
    photos,
    weekly_availability: {},
  });

  const submit = async (e, asDraft = false) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = buildBody();
      if (editing) {
        await api.patch(`/listings/${id}`, body);
        toast.success(status === "draft" ? "Draft saved." : "Listing updated — back in review before it re-lists.");
      } else {
        await api.post(`/listings${asDraft ? "?draft=true" : ""}`, body);
        toast.success(asDraft ? "Draft saved to your dashboard." : "Listing submitted for review — we'll notify you once it's approved and live.");
      }
      navigate("/host");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save listing");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-2">{editing ? "EDIT LISTING" : "NEW LISTING"}</div>
      <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-8">{editing ? "Edit your space." : "List your space."}</h1>

      <form onSubmit={(e) => submit(e, false)} className="space-y-8" data-testid="create-listing-form">
        {/* Photos */}
        <section className="border border-neutral-200 rounded-xl p-6 bg-white">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 mb-3">PHOTOS</div>
          <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
            {photos.map((p) => (
              <div key={p} className="relative aspect-square border border-neutral-200 rounded-lg overflow-hidden">
                <img src={imgSrc(p)} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => setPhotos((ph) => ph.filter((x) => x !== p))}
                  className="absolute top-1 right-1 bg-white/90 rounded-full p-1 hover:bg-white"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="aspect-square border-2 border-dashed border-neutral-300 rounded-lg grid place-items-center hover:border-[#FF4500] transition-colors"
              data-testid="upload-photo-btn"
            >
              <div className="text-center">
                <Upload className="w-5 h-5 mx-auto text-neutral-400" />
                <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-2">
                  {uploading ? "…" : "Add"}
                </div>
              </div>
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => upload(Array.from(e.target.files))} />
          </div>
        </section>

        <section className="border border-neutral-200 rounded-xl p-6 bg-white grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <Label>Title</Label>
            <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-2 h-11" data-testid="listing-title" />
          </div>
          <div className="md:col-span-2">
            <Label>Description</Label>
            <Textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2" data-testid="listing-description" />
          </div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="mt-2 h-11" data-testid="listing-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Capacity</Label>
            <Input type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="mt-2 h-11" data-testid="listing-capacity" />
          </div>
          <div>
            <Label>City</Label>
            <Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-2 h-11" data-testid="listing-city" />
          </div>
          <div>
            <Label>Address</Label>
            <Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-2 h-11" data-testid="listing-address" />
          </div>
          <div>
            <Label>Your rate / hour (USD) — you keep 100%</Label>
            <Input type="number" min="1" required value={form.price_per_hour} onChange={(e) => setForm({ ...form, price_per_hour: e.target.value })} className="mt-2 h-11" data-testid="listing-price" />
            <p className="text-[11px] text-neutral-500 mt-1">Renters pay this plus a 15% SpareHours service fee. You receive your full rate.</p>
          </div>
          <div>
            <Label>Refundable deposit (USD)</Label>
            <Input type="number" min="0" value={form.deposit} onChange={(e) => setForm({ ...form, deposit: e.target.value })} placeholder="0 = no deposit" className="mt-2 h-11" data-testid="listing-deposit" />
            <p className="text-[11px] text-neutral-500 mt-1">Held at checkout, refunded after a clean booking. Leave 0 for none.</p>
          </div>
          <div>
            <Label>Amenities (comma separated)</Label>
            <Input value={form.amenities} onChange={(e) => setForm({ ...form, amenities: e.target.value })} placeholder="Wifi, Sound system, Parking" className="mt-2 h-11" data-testid="listing-amenities" />
          </div>
          <div className="md:col-span-2">
            <Label>House rules</Label>
            <Textarea rows={3} value={form.rules} onChange={(e) => setForm({ ...form, rules: e.target.value })} className="mt-2" data-testid="listing-rules" />
          </div>
        </section>

        <div className="flex flex-wrap gap-3">
          <Button type="submit" disabled={saving} className="bg-[#FF4500] hover:bg-[#E63E00] text-white rounded-full h-12 px-8" data-testid="listing-submit-btn">
            {saving ? "Saving…" : editing ? (status === "draft" ? "Save draft" : "Save & resubmit") : "Submit for review"}
          </Button>
          {!editing && (
            <Button type="button" variant="outline" disabled={saving} onClick={(e) => submit(e, true)} className="rounded-full h-12 px-8" data-testid="listing-draft-btn">
              Save as draft
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
